import React, { useState, useMemo, useEffect } from 'react';
import { matchEintrag, confidenceLabel } from '../services/smartMatcher';
import { type KalenderEintrag, type SmartMatchResult } from '../types';
import { type MandantID } from '../../../types/branded';
import { LEISTUNG_MAP } from '../../abrechnung/data/leistungskatalog';
import { listEventsMulti, isTokenValid } from '../../../services/googleCalendar';
import { loadSetting } from '../../../services/settingsService';
import { useMandantStore, type MandantConfig } from '../../../store/mandantStore';
import { addZeitEintrag, loadZeiteintraege } from '../../../services/zeitService';

// ── Firmen-Zuordnung eines Termins (Titel/Ort gegen Firmen-Aliase) ─────────

function matchFirma(titel: string, ort: string, mandanten: MandantConfig[]): MandantConfig | null {
  const hay = `${titel} ${ort}`.toLowerCase();
  for (const m of mandanten) {
    if (m.title_aliases.some(a => a !== '' && hay.includes(a.toLowerCase()))) return m;
    if (m.location_keywords.some(k => k !== '' && hay.includes(k.toLowerCase()))) return m;
    if (m.name !== '' && hay.includes(m.name.toLowerCase())) return m;
  }
  return null;
}

function formatUhrzeit(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}

function formatDatum(iso: string): string {
  return new Date(iso).toLocaleDateString('de-AT', {
    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// ── Einzelne Review-Karte ─────────────────────────────────────────────────

type EntryState = 'pending' | 'confirmed' | 'rejected';

function ReviewKarte({
  eintrag,
  firma,
  match,
  state,
  onConfirm,
  onReject,
}: {
  eintrag:   KalenderEintrag;
  firma:     string;
  match:     SmartMatchResult;
  state:     EntryState;
  onConfirm: () => void;
  onReject:  () => void;
}): React.JSX.Element {
  const cl       = confidenceLabel(match.confidence);
  const leistung = LEISTUNG_MAP.get(match.leistungCode);

  return (
    <div className={`card space-y-3 border-l-4 transition-all
        ${state === 'confirmed' ? 'border-l-green-500 opacity-70'
        : state === 'rejected'  ? 'border-l-red-400 opacity-50'
        :                         'border-l-blue-500'
        }`}>
      {/* Zeitinfo + Titel */}
      <div className="flex items-start gap-3">
        <div className="text-xs text-gray-400 font-mono w-12 flex-shrink-0 pt-0.5">
          {formatUhrzeit(eintrag.startISO)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{eintrag.titel}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {formatDatum(eintrag.startISO)} · {formatUhrzeit(eintrag.startISO)} – {formatUhrzeit(eintrag.endeISO)} · {firma}
          </p>
        </div>
        {/* Status-Icon */}
        {state === 'confirmed' && <span className="text-green-600 flex-shrink-0">✓</span>}
        {state === 'rejected'  && <span className="text-red-400 flex-shrink-0">✗</span>}
      </div>

      {/* Match-Ergebnis */}
      {match.kategorie !== 'Unbekannt' ? (
        <div className="flex flex-wrap items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cl.color}`}>
            {cl.text} {match.confidence}%
          </span>
          <span className="text-xs font-mono text-gray-500">{match.leistungCode}</span>
          {leistung !== undefined && (
            <span className="text-xs text-gray-700 dark:text-gray-300">{leistung.bezeichnung}</span>
          )}
          {match.tatbestandCode !== undefined && (
            <span className="text-xs bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded font-mono">
              {match.tatbestandCode}
            </span>
          )}
          <span className="text-xs text-gray-400 ml-auto hidden sm:block">{match.grund}</span>
        </div>
      ) : (
        <div className="bg-orange-50 dark:bg-orange-950/20 rounded-lg px-3 py-2">
          <p className="text-xs text-orange-600 dark:text-orange-400">
            Kein Match erkannt — bitte manuell zuordnen
          </p>
        </div>
      )}

      {/* Aktionen */}
      {state === 'pending' && (
        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            className="flex-1 text-sm py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors font-medium"
          >
            ✓ Bestätigen
          </button>
          <button
            onClick={onReject}
            className="flex-1 text-sm py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
          >
            ✗ Ablehnen
          </button>
        </div>
      )}
      {state === 'confirmed' && (
        <p className="text-xs text-green-600 font-medium">Bestätigt — wird in Abrechnung übernommen</p>
      )}
      {state === 'rejected' && (
        <button onClick={onConfirm} className="text-xs text-gray-400 hover:text-gray-600">
          Rückgängig
        </button>
      )}
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

export function EndOfDayReview(): React.JSX.Element {
  const mandanten = useMandantStore(s => s.mandanten);
  const [eintraege, setEintraege] = useState<KalenderEintrag[]>([]);
  const [states, setStates] = useState<Map<string, EntryState>>(new Map());
  const [gebucht, setGebucht] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [meldung, setMeldung] = useState('');

  useEffect(() => {
    void (async () => {
      if (!isTokenValid()) {
        setMeldung('Google Kalender nicht verbunden — im Tab „Kalender" verbinden.');
        setLoading(false); return;
      }
      try {
        const cfg = await loadSetting<{ calendarId?: string; calendarIds?: string[] } | null>('gcal_config', null);
        const cals = cfg?.calendarIds !== undefined && cfg.calendarIds.length > 0
          ? cfg.calendarIds
          : [cfg?.calendarId ?? 'primary'];
        const start = new Date(); start.setHours(0, 0, 0, 0);
        const ende  = new Date(); ende.setHours(23, 59, 59, 999);
        const evs = await listEventsMulti(cals, start.toISOString(), ende.toISOString());
        const list: KalenderEintrag[] = evs.map(ev => {
          const firma = matchFirma(ev.summary ?? '', ev.location ?? '', mandanten);
          return {
            id: ev.id,
            mandantId: firma !== null ? firma.id : ('' as MandantID),
            titel: ev.summary ?? '(ohne Titel)',
            startISO: ev.start.dateTime ?? ev.start.date ?? '',
            endeISO: ev.end.dateTime ?? ev.end.date ?? '',
            quelle: 'google', status: 'unmatched', erstelltAm: new Date().toISOString(),
          };
        });
        // Bereits gebuchte Termine (gleicher Start + Firma) als bestätigt zeigen.
        const existing = await loadZeiteintraege();
        const bookedKeys = new Set(existing.map(z => `${z.startISO}|${z.mandantId}`));
        setGebucht(bookedKeys);
        setEintraege(list);
        const m = new Map<string, EntryState>();
        for (const x of list) {
          m.set(x.id, bookedKeys.has(`${x.startISO}|${x.mandantId}`) ? 'confirmed' : 'pending');
        }
        setStates(m);
      } catch (err) {
        setMeldung(`Kalender konnte nicht geladen werden: ${err instanceof Error ? err.message : String(err)}`);
      }
      setLoading(false);
    })();
  }, [mandanten]);

  const matches = useMemo(() =>
    new Map(eintraege.map(e => [e.id, matchEintrag(e)]))
  , [eintraege]);

  const setState = (id: string, s: EntryState): void => {
    setStates(prev => { const n = new Map(prev); n.set(id, s); return n; });
  };

  // „Bestätigen" → Zeiteintrag anlegen (einmal je Termin; Dedup über Start+Firma).
  async function confirm(e: KalenderEintrag, match: SmartMatchResult): Promise<void> {
    const key = `${e.startISO}|${e.mandantId}`;
    if (!gebucht.has(key)) {
      const dauerMin = e.endeISO !== '' && e.endeISO !== null
        ? Math.max(0, Math.round((new Date(e.endeISO).getTime() - new Date(e.startISO).getTime()) / 60000))
        : 0;
      await addZeitEintrag({
        id: crypto.randomUUID(),
        datum: e.startISO.slice(0, 10),
        startISO: e.startISO,
        endeISO: e.endeISO !== '' ? e.endeISO : null,
        dauerMin,
        mandantId: e.mandantId,
        leistungCode: match.leistungCode,
        beschreibung: e.titel,
        abgerechnet: false,
      });
      setGebucht(prev => new Set(prev).add(key));
    }
    setState(e.id, 'confirmed');
  }

  const confirmed = [...states.values()].filter(s => s === 'confirmed').length;
  const pending   = [...states.values()].filter(s => s === 'pending').length;
  const today     = new Date().toLocaleDateString('de-AT', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="p-6 space-y-5 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">End-of-Day Review</h1>
          <p className="text-sm text-gray-500">{today}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{confirmed}/{eintraege.length}</p>
          <p className="text-xs text-gray-400">bestätigt</p>
        </div>
      </div>

      {/* Progress */}
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs text-gray-500">
          <span>{pending} ausstehend</span>
          <span>{confirmed} bestätigt</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className="bg-green-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${eintraege.length > 0 ? (confirmed / eintraege.length) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Zustände / Karten */}
      {loading ? (
        <div className="card text-center py-10 text-gray-400 text-sm">Kalender wird geladen…</div>
      ) : meldung !== '' ? (
        <div className="card text-center py-10 text-gray-500 text-sm">{meldung}</div>
      ) : eintraege.length === 0 ? (
        <div className="card text-center py-10 text-gray-400 space-y-2">
          <p className="text-3xl">📅</p>
          <p className="text-sm">Heute keine Termine im gewählten Kalender.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {eintraege.map(e => {
            const m = matches.get(e.id);
            const s = states.get(e.id) ?? 'pending';
            if (m === undefined) { return null; }
            return (
              <ReviewKarte
                key={e.id}
                eintrag={e}
                firma={mandanten.find(md => md.id === e.mandantId)?.kurz_code ?? '—'}
                match={m}
                state={s}
                onConfirm={() => { void confirm(e, m); }}
                onReject={() => { setState(e.id, 'rejected'); }}
              />
            );
          })}
        </div>
      )}

      {!loading && meldung === '' && eintraege.length > 0 && pending === 0 && (
        <div className="card text-center py-8 space-y-2 border-green-200 dark:border-green-800">
          <p className="text-3xl">✅</p>
          <p className="font-semibold text-green-700 dark:text-green-400">Review abgeschlossen!</p>
          <p className="text-sm text-gray-500">{confirmed} Termine als Zeiteinträge gebucht — im Modul „Zeit &amp; Leistung" sichtbar.</p>
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">
        Echte Google-Kalender-Termine von heute · „Bestätigen" bucht einen Zeiteintrag
      </p>
    </div>
  );
}
